import { v4 as uuidv4 } from 'uuid'

import { normalizeDashboards } from '../../../../utils'
import { history } from '@edge/spark/utils'
import { DASHBOARD_LIMIT } from '../../../../constants'
import {
    ClientUserDashboardsDocument as query,
    type ClientUserDashboardsQuery,
    UserAuthenticationIdDocument as userIdQuery,
    type UserAuthenticationIdQuery,
    type MutationResolvers,
    UpdateUserDashboardsDocument as mutation,
    type UpdateUserDashboardsMutation,
} from '../../../../generated/graphql'
import { createDialog } from '@edge/spark/feature-dialog'
import { getActiveDashboardId, setActiveDashboardId } from '../../../../Stores/activeDashboardId'
import type { ApolloResolverContext } from '@edge/spark/io-apollo'
import { getActiveAuctionId } from '@edge/spark/io-roles'

const copyDashboard: MutationResolvers<ApolloResolverContext>['copyDashboard'] = async (
    _root,
    { userId, name, priorDashId, priorUserId },
    { cache, client },
) => {
    console.debug('io-dashboards copyDashboard Deprecated, refactor (soon)')

    if (!client) {
        throw new Error('Missing ApolloClient')
    }

    const auctionId = getActiveAuctionId()

    if (!auctionId) {
        throw new Error('copyDashboard: Missing AuctionId')
    }

    createDialog({
        message: 'Copying dashboard',
        action: 'SPINNER',
        timeout: 10000,
    })

    if (priorUserId && !priorDashId) {
        throw new Error('Custom userId without dashId')
    }

    const { data: userIdData } = await client.query<UserAuthenticationIdQuery>({
        query: userIdQuery,
    })

    const userIdIsSelf = !userId || userId === userIdData.user?.attributes?.authenticationId

    const { data } = await client.query<ClientUserDashboardsQuery>({
        query,
        variables: { userId },
        fetchPolicy: 'network-only',
    })

    const savedDashboards = normalizeDashboards(data.dashboards)

    if (savedDashboards.length >= DASHBOARD_LIMIT) {
        createDialog({
            action: 'OK',
            message: `Limit ${DASHBOARD_LIMIT} Dashboards`,
            timeout: 5000,
        })

        return false
    }

    const { data: priorUserData } = await client.query<ClientUserDashboardsQuery>({
        query,
        variables: { userId: priorUserId },
        fetchPolicy: 'network-only',
    })

    const priorUserSavedDashboards = normalizeDashboards(priorUserData?.dashboards)
    const activeDashboardId = getActiveDashboardId()
    const priorDashboardToCopyIndex = priorUserSavedDashboards.findIndex(
        (d) => d.id === (priorDashId || activeDashboardId),
    )
    const nextDashId = uuidv4()
    const updatedDashboard = {
        ...priorUserSavedDashboards[priorDashboardToCopyIndex],
        id: nextDashId,
        name,
    }
    const updatedDashboards = savedDashboards.concat(updatedDashboard)
    const stringifiedUpdatedDashboards = updatedDashboards.map((d) => JSON.stringify(d))
    const admin = userIdIsSelf ? undefined : { auctionId, subjectUserId: userId }

    await client.mutate<UpdateUserDashboardsMutation>({
        mutation,
        variables: { admin, dashboards: stringifiedUpdatedDashboards },
        optimisticResponse: {
            updateUser: {
                __typename: 'UserMutationResponse',
                authenticationId: null,
                statusCode: 200,
                statusMessage: null,
            },
        },
        update: (store): void => {
            setActiveDashboardId(nextDashId)
            history.push('/')

            if (userId) {
                store.writeData({
                    data: {
                        [`dashboards({"userId":"${userId}"})`]: stringifiedUpdatedDashboards,
                        administrator: {
                            __typename: 'Administrator',
                            [`user({"input":{"admin":{"auctionId":"${auctionId}","subjectUserId":"${userId}"}}})`]: {
                                __typename: 'User',
                                dashboards: stringifiedUpdatedDashboards,
                            },
                        },
                    },
                })
            }

            if (userIdIsSelf) {
                store.writeData({
                    data: {
                        'dashboards({})': stringifiedUpdatedDashboards,
                        user: {
                            __typename: 'User',
                            dashboards: stringifiedUpdatedDashboards,
                        },
                    },
                })
            }
        },
    })

    createDialog({
        action: 'OK',
        message: `Copied${name ? ` '${name}'` : ''} dashboard`,
        timeout: 5000,
    })

    return true
}

export default copyDashboard
